import type { PmTimelinePicker, PmTimelinePickerButton } from '@cortex/web-api/clients/admin/api';
import { formatTimestamp } from './format.js';

/**
 * A picker card in the admin conversation viewer — the inline-keyboard prompt
 * the agent sent the PM (e.g. "pick tenant", "email vs Buildium"). Picker cards
 * aren't stored in the messages table, so they're merged in from
 * pending_approvals. The card renders the reconstructed Telegram buttons and
 * highlights (green) the one the PM tapped. Sits on the agent (right) side.
 *
 * Picker kinds with no button reconstruction (vendor/N4/N1) arrive with empty
 * `buttonRows` — the card still shows the summary + a state/tapped footer.
 */
export function PickerCard({ picker }: { picker: PmTimelinePicker }) {
  const pressed = picker.buttonRows.flat().find((b) => b.pressed) ?? null;
  const dimOthers = picker.state !== 'awaiting' && pressed !== null;
  const footer = footerText(picker, pressed);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <div
        style={{
          maxWidth: '88%',
          background: 'var(--paper)',
          border: '1px solid var(--rule)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-subtle)',
          padding: 'var(--space-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 'var(--fs-mono-sm)',
            color: 'var(--muted)',
            letterSpacing: 'var(--tracking-mono)',
          }}
        >
          picker · {picker.approvalKind.replace(/^pm_/, '').replace(/_/g, ' ')}
        </div>
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-small)',
            lineHeight: 'var(--lh-body)',
            color: 'var(--ink-soft)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {picker.summary}
        </div>
        {picker.buttonRows.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-1)',
              marginTop: 'var(--space-1)',
            }}
          >
            {picker.buttonRows.map((row, ri) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static reconstructed keyboard, never reordered
              <div key={ri} style={{ display: 'flex', gap: 'var(--space-1)' }}>
                {row.map((btn, bi) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: static reconstructed keyboard, never reordered
                  <PickerButton key={bi} btn={btn} dimmed={dimOthers && !btn.pressed} />
                ))}
              </div>
            ))}
          </div>
        )}
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: 'var(--tracking-mono)',
            color: footer.highlight ? 'var(--green-deep)' : 'var(--muted)',
          }}
        >
          {footer.text}
        </div>
      </div>
    </div>
  );
}

function PickerButton({ btn, dimmed }: { btn: PmTimelinePickerButton; dimmed: boolean }) {
  return (
    <div
      style={{
        flex: 1,
        textAlign: 'center',
        padding: '8px 10px',
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-small)',
        fontWeight: btn.pressed ? 600 : 400,
        background: btn.pressed ? 'var(--green)' : 'var(--paper-2)',
        color: btn.pressed ? 'var(--paper)' : 'var(--ink)',
        border: btn.pressed ? '1px solid var(--green-deep)' : '1px solid var(--rule)',
        opacity: dimmed ? 0.5 : 1,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {btn.pressed ? `✓ ${btn.label}` : btn.label}
    </div>
  );
}

/**
 * The footer caption under the card. Mirrors the four outcomes the data
 * encodes: still awaiting, tapped a button, replied in free-text instead of
 * tapping (superseded with no decided action), or expired with no response.
 */
function footerText(
  picker: PmTimelinePicker,
  pressed: PmTimelinePickerButton | null,
): { text: string; highlight: boolean } {
  const when = picker.decidedAt ? ` · ${formatTimestamp(picker.decidedAt)}` : '';
  if (picker.state === 'awaiting') {
    return { text: 'awaiting PM tap', highlight: false };
  }
  const tapped = picker.decidedLabel ?? pressed?.label ?? null;
  if (tapped) {
    return { text: `PM tapped: ${tapped}${when}`, highlight: true };
  }
  if (picker.state === 'superseded') {
    return { text: `PM replied in chat instead${when}`, highlight: false };
  }
  if (picker.state === 'expired') {
    return { text: 'expired — no response', highlight: false };
  }
  if (picker.state === 'denied') {
    return { text: `cancelled${when}`, highlight: false };
  }
  return { text: `${picker.state}${when}`, highlight: false };
}
